import { DomainRegistry } from "../../../../domain";
import { IContentSource } from "../../../../domain/drawing";
import { FixedPhraseRootReference, IFixedPhraseRepository } from "../../../../domain/document";
import { ManagementTokenPublishResult } from "../../../../domain/system";
import { FixedPhraseKinds } from "../";

export interface IPhraseAccessible {
  readonly name: string;
  get(id?: string): Promise<FixedPhraseRootReference>;
  set(source: FixedPhraseRootReference): Promise<string>;
  export(isJsonFile: boolean, id?: string): Promise<IContentSource>;
  shouldManageToken(): boolean;
  getToken(isForcibly: boolean, id?: string): Promise<ManagementTokenPublishResult>;
}

export abstract class AbstractPhraseAccessor<T> implements IPhraseAccessible {
  constructor(readonly name: string) {}

  protected abstract createInstance(): T;

  async get(id?: string): Promise<FixedPhraseRootReference> {
    return this.getPhrases(this.createInstance(), id);
  }

  protected abstract getPhrases(instance: T, id?: string): Promise<FixedPhraseRootReference>;

  async set(source: FixedPhraseRootReference): Promise<string> {
    return this.setPhrases(this.createInstance(), source);
  }

  protected abstract setPhrases(instance: T, source: FixedPhraseRootReference): Promise<string>;

  async export(isJsonFile: boolean, id?: string): Promise<IContentSource> {
    return this.exportPhrases(this.createInstance(), isJsonFile, id);
  }

  protected abstract exportPhrases(instance: T, isJsonFile: boolean, id?: string): Promise<IContentSource>;

  abstract shouldManageToken(): boolean;

  abstract getToken(isForcibly: boolean, id?: string): Promise<ManagementTokenPublishResult>;
}

export abstract class AbstractFixedPhraseAccessor extends AbstractPhraseAccessor<IFixedPhraseRepository> {
  constructor(prefix: string) {
    super(prefix + "定型文");
  }

  protected createInstance(): IFixedPhraseRepository {
    return DomainRegistry.fixedPhrase.getFixedPhraseRepository();
  }

  protected abstract getPhrases(repository: IFixedPhraseRepository, id?: string): Promise<FixedPhraseRootReference>;

  protected abstract setPhrases(instance: IFixedPhraseRepository, source: FixedPhraseRootReference): Promise<string>;

  protected abstract exportPhrases(repository: IFixedPhraseRepository, isJsonFile: boolean, id?: string): Promise<IContentSource>;

  abstract shouldManageToken(): boolean;

  abstract getToken(isForcibly: boolean, id?: string): Promise<ManagementTokenPublishResult>;
}

export class FixedPhraseAccessor extends AbstractFixedPhraseAccessor {
  constructor(prefix: string, private readonly _implementation: {
    get: (r: IFixedPhraseRepository, id?: string) => Promise<FixedPhraseRootReference>,
    set: (r: IFixedPhraseRepository, source: FixedPhraseRootReference) => Promise<string>,
    export: (r: IFixedPhraseRepository, isJsonFile: boolean, id?: string) => Promise<IContentSource>,
    getToken?: (r: IFixedPhraseRepository, isForcibly: boolean, id?: string) => Promise<ManagementTokenPublishResult>,
  }) {
    super(prefix);
  }

  protected getPhrases(repository: IFixedPhraseRepository, id?: string): Promise<FixedPhraseRootReference> {
    return this._implementation.get(repository, id);
  }

  protected setPhrases(repository: IFixedPhraseRepository, source: FixedPhraseRootReference): Promise<string> {
    return this._implementation.set(repository, source);
  }

  protected exportPhrases(repository: IFixedPhraseRepository, isJsonFile: boolean, id?: string): Promise<IContentSource> {
    return this._implementation.export(repository, isJsonFile, id);
  }

  shouldManageToken(): boolean {
    return !!this._implementation.getToken;
  }

  getToken(isForcibly: boolean, id?: string): Promise<ManagementTokenPublishResult> {
    return this._implementation.getToken(this.createInstance(), isForcibly, id);
  }
}

export class FixedPhraseAccessorFactory {
  constructor(wordings: { department?: string, note?: string }) {
    const complementWordings = {
      department: wordings.department ? wordings.department : "診療科",
      note: wordings.note ? wordings.note : "文書メモ",
    };
    this._accessors = {
      [FixedPhraseKinds.User]: new FixedPhraseAccessor("ユーザ", {
        get: (r, id) => r.getUserFixedPhrase(id),
        set: (r, s) => r.setUserFixedPhrase(s),
        export: (r, isJsonFile, id) => r.exportUserFixedPhrase(id, isJsonFile),
      }),
      [FixedPhraseKinds.Department]: new FixedPhraseAccessor("主" + complementWordings.department, {
        get: (r, id) => r.getDepartmentFixedPhrase(id),
        set: (r, s) => r.setDepartmentFixedPhrase(s),
        export: (r, isJsonFile, id) => r.exportDepartmentFixedPhrase(id, isJsonFile),
        getToken: (r, isForcibly, id) => r.getDepartmentFixedPhraseManagementToken(id, isForcibly),
      }),
      [FixedPhraseKinds.CommonDepartment]: new FixedPhraseAccessor("全" + complementWordings.department, {
        get: (r) => r.getCommonDepartmentFixedPhrase(),
        set: (r, s) => r.setCommonDepartmentFixedPhrase(s),
        export: (r, isJsonFile) => r.exportCommonDepartmentFixedPhrase(isJsonFile),
        getToken: (r, isForcibly) => r.getCommonDepartmentFixedPhraseManagementToken(isForcibly),
      }),
      [FixedPhraseKinds.Format]: new FixedPhraseAccessor("フォーマット", {
        get: (r, id) => r.getFormatFixedPhrase(id),
        set: (r, s) => r.setFormatFixedPhrase(s),
        export: (r, isJsonFile, id) => r.exportFormatFixedPhrase(id, isJsonFile),
        getToken: (r, isForcibly, id) => r.getFormatFixedPhraseManagementToken(id, isForcibly),
      }),
      [FixedPhraseKinds.TemporarySaveUser]: new FixedPhraseAccessor("一時保存・" + complementWordings.note, {
        get: (r, id) => r.getTemporarySaveUserFixedPhrase(id),
        set: (r, s) => r.setTemporarySaveUserFixedPhrase(s),
        export: (r, isJsonFile, id) => r.exportTemporarySaveUserFixedPhrase(id, isJsonFile),
      }),
      [FixedPhraseKinds.ReceptionUser]: new FixedPhraseAccessor("ユーザ", {
        get: (r, id) => r.getReceptionUserFixedPhrase(id),
        set: (r, s) => r.setReceptionUserFixedPhrase(s),
        export: (r, isJsonFile, id) => r.exportReceptionUserTemplatePhrase(id, isJsonFile),
      }),
      [FixedPhraseKinds.ReceptionDepartment]: new FixedPhraseAccessor("主" + complementWordings.department, {
        get: (r, id) => r.getReceptionDepartmentFixedPhrase(id),
        set: (r, s) => r.setReceptionDepartmentFixedPhrase(s),
        export: (r, isJsonFile, id) => r.exportReceptionDepartmentTemplatePhrase(id, isJsonFile),
        getToken: (r, isForcibly, id) => r.getReceptionDepartmentFixedPhraseManagementToken(id, isForcibly),
      }),
      [FixedPhraseKinds.AiUser]: new FixedPhraseAccessor("ユーザ生成AI", {
        get: (r, id) => r.getAiUserFixedPhrase(id),
        set: (r, s) => r.setAiUserFixedPhrase(s),
        export: (r, isJsonFile, id) => r.exportAiUserFixedPhrase(id, isJsonFile),
      }),
      [FixedPhraseKinds.AiDepartment]: new FixedPhraseAccessor("主" + complementWordings.department + "生成AI", {
        get: (r, id) => r.getAiDepartmentFixedPhrase(id),
        set: (r, s) => r.setAiDepartmentFixedPhrase(s),
        export: (r, isJsonFile, id) => r.exportAiDepartmentFixedPhrase(id, isJsonFile),
        getToken: (r, isForcibly, id) => r.getAiDepartmentFixedPhraseManagementToken(id, isForcibly),
      }),
      [FixedPhraseKinds.AiFormat]: new FixedPhraseAccessor("フォーマット生成AI", {
        get: (r, id) => r.getAiFormatFixedPhrase(id),
        set: (r, s) => r.setAiFormatFixedPhrase(s),
        export: (r, isJsonFile, id) => r.exportAiFormatFixedPhrase(id, isJsonFile),
        getToken: (r, isForcibly, id) => r.getAiFormatFixedPhraseManagementToken(id, isForcibly),
      }),
      [FixedPhraseKinds.AiCommonDepartment]: new FixedPhraseAccessor("全" + complementWordings.department + "生成AI", {
        get: (r) => r.getAiCommonDepartmentFixedPhrase(),
        set: (r, s) => r.setAiCommonDepartmentFixedPhrase(s),
        export: (r, isJsonFile) => r.exportAiCommonDepartmentFixedPhrase(isJsonFile),
        getToken: (r, isForcibly) => r.getAiCommonDepartmentFixedPhraseManagementToken(isForcibly),
      }),
    };
  }
  
  private readonly _accessors: { [key in FixedPhraseKinds]: IPhraseAccessible };

  get(key: FixedPhraseKinds): IPhraseAccessible {
    return this._accessors[key];
  }
}
